import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Sparkles } from 'lucide-react';
import { cn } from 'lib/utils';
import { formatGroupedNumber } from 'lib/dateFormat';
import { Panel } from 'components/ui/panel';
import { Progress } from 'components/ui/progress';
import { Badge } from 'components/ui/badge';
import { StatusBadge } from 'components/ui/status-badge';
import EmptyState from 'components/ui/EmptyState';
import Skeleton from 'components/ui/skeleton';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from 'components/ui/table';
import type { IEntity } from 'modules/auth/types';
import type { ChatTier } from 'modules/llm/api';
import type { TokenUsageBreakdown } from 'modules/entitlements';
import { formatDate } from './billingHelpers';

type Feature = IEntity.FeatureEntitlement | undefined;

function resourceLimit(feature: Feature): number | null {
  if (!feature?.enabled) return 0;
  return feature.limit ?? null;
}

/** Usage turns amber from this share of the limit, red at the limit. */
const WARN_AT_PCT = 80;

interface UsageRowProps {
  label: string;
  used: number;
  /** null = unlimited; 0 = not in the plan. */
  limit: number | null;
}

/** Label and "used / limit" over an ink bar; amber near the limit, red at it. */
function UsageRow({ label, used, limit }: UsageRowProps) {
  const { t } = useTranslation();
  const notIncluded = limit === 0 && used === 0;
  const hasBar = limit !== null && !notIncluded;
  const pct = limit ? Math.min(100, (used / limit) * 100) : 100;
  const atLimit = hasBar && pct >= 100;
  const nearLimit = hasBar && !atLimit && pct >= WARN_AT_PCT;

  let value: string;
  if (notIncluded) value = t('not_included', 'Not included');
  else if (limit === null) value = `${formatGroupedNumber(used)} / ${t('unlimited', 'Unlimited')}`;
  else value = `${formatGroupedNumber(used)} / ${formatGroupedNumber(limit)}`;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3 text-[13px]">
        <span className="min-w-0 truncate text-foreground">{label}</span>
        <span
          className={cn(
            'shrink-0 tabular-nums',
            atLimit
              ? 'font-medium text-error-700 dark:text-error-400'
              : nearLimit
                ? 'font-medium text-warning-700 dark:text-warning-400'
                : 'text-muted-foreground',
          )}
        >
          {value}
        </span>
      </div>
      {hasBar && (
        <Progress
          value={used}
          max={Math.max(1, limit ?? 1)}
          size="sm"
          variant={atLimit ? 'danger' : nearLimit ? 'warning' : 'primary'}
        />
      )}
    </div>
  );
}

interface ResourceUsageCardProps {
  counts?: { assessments: number; members: number; documents: number };
  loading: boolean;
  assessments: Feature;
  members: Feature;
  documents: Feature;
  aiTokens: Feature;
}

/** Live resource counters against the plan's limits. */
export function ResourceUsageCard({ counts, loading, assessments, members, documents, aiTokens }: ResourceUsageCardProps) {
  const { t } = useTranslation();
  const rows: Array<UsageRowProps & { key: string }> = [
    { key: 'assessments', label: t('usage_tests', 'Tests'), used: counts?.assessments ?? 0, limit: resourceLimit(assessments) },
    { key: 'members', label: t('organization_members', 'Organization members'), used: counts?.members ?? 0, limit: resourceLimit(members) },
    { key: 'documents', label: t('learning_documents', 'Learning documents'), used: counts?.documents ?? 0, limit: resourceLimit(documents) },
  ];
  if (aiTokens?.enabled) {
    rows.push({ key: 'ai_tokens', label: t('ai_tokens_monthly', 'AI Tokens (monthly)'), used: aiTokens.used ?? 0, limit: aiTokens.limit ?? null });
  }

  return (
    <Panel headingLevel="h3" title={t('resource_usage', 'Resource usage')} aside={<StatusBadge tone="success">{t('live', 'Live')}</StatusBadge>}>
      <p className="mb-4 text-[13px] text-muted-foreground">
        {t(
          'live_counts_u2014_limits_are_enforced_atomically_at_creation_time',
          'Live counts — limits are enforced atomically at creation time.',
        )}
      </p>
      {loading ? (
        <div className="flex flex-col gap-4" aria-hidden>
          {[0, 1, 2].map((row) => (
            <div key={row} className="flex flex-col gap-2">
              <div className="flex justify-between">
                <Skeleton className="h-3.5 w-28" />
                <Skeleton className="h-3.5 w-16" />
              </div>
              <Skeleton className="h-1 w-full" />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {rows.map(({ key, ...row }) => (
            <UsageRow key={key} {...row} />
          ))}
        </div>
      )}
    </Panel>
  );
}

interface AiTiersCardProps {
  tiers: ChatTier[];
}

/**
 * The AI capability ladder for the current plan.
 *
 * Shows locked rungs too — this is the billing page, so what the next plan up
 * unlocks is the most useful thing on it.
 */
export function AiTiersCard({ tiers }: AiTiersCardProps) {
  const { t } = useTranslation();

  return (
    <Panel headingLevel="h3" title={t('ai_response_quality', 'AI response quality')}>
      <p className="text-[13px] text-muted-foreground">
        {t('tiers_available_on_your_plan', 'Quality levels you can pick in AI chat')}
      </p>
      {tiers.length > 0 ? (
        <ul className="mt-2 divide-y divide-border">
          {tiers.map((tier) => (
            <li key={tier.key} className="flex items-center gap-3 py-2.5 last:pb-0">
              <span
                aria-hidden
                className={cn('h-1.5 w-1.5 shrink-0 rounded-full', tier.locked ? 'bg-muted-foreground/40' : 'bg-foreground')}
              />
              <div className="min-w-0 flex-1">
                <p className={cn('truncate text-[13.5px] font-medium', tier.locked ? 'text-muted-foreground' : 'text-foreground')}>
                  {tier.label}
                </p>
                {tier.description && <p className="truncate text-[12.5px] text-muted-foreground">{tier.description}</p>}
              </div>
              {tier.locked && (
                <Badge className="shrink-0 gap-1">
                  <Lock className="h-3 w-3" aria-hidden />
                  {t('upgrade', 'Upgrade')}
                </Badge>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-[13px] text-muted-foreground">
          {t('no_ai_models_on_plan', 'No AI models are enabled on your current plan.')}
        </p>
      )}
    </Panel>
  );
}

interface TokenUsageByMemberCardProps {
  tokenUsage?: TokenUsageBreakdown;
  loading: boolean;
}

/** Per-member token breakdown for the current billing period. */
export function TokenUsageByMemberCard({ tokenUsage, loading }: TokenUsageByMemberCardProps) {
  const { t } = useTranslation();
  const rows = tokenUsage?.byUser ?? [];

  let body: React.ReactNode;
  if (loading) {
    body = (
      <div className="divide-y divide-border" aria-hidden>
        {[0, 1, 2].map((row) => (
          <div key={row} className="flex items-center justify-between gap-4 px-4 py-3">
            <Skeleton className="h-3.5 w-40" />
            <Skeleton className="h-3.5 w-20" />
          </div>
        ))}
      </div>
    );
  } else if (rows.length === 0) {
    body = (
      <EmptyState
        icon={<Sparkles />}
        title={t('no_ai_usage_recorded_yet_this_period', 'No AI usage recorded yet this period.')}
        className="py-9"
      />
    );
  } else {
    body = (
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>{t('member', 'Member')}</TableHead>
            {/* Phones keep the total; the prompt/completion split needs the width. */}
            <TableHead className="hidden text-right sm:table-cell">{t('prompt', 'Prompt')}</TableHead>
            <TableHead className="hidden text-right sm:table-cell">{t('completion', 'Completion')}</TableHead>
            <TableHead className="text-right">{t('total', 'Total')}</TableHead>
            <TableHead className="text-right">{t('last_used', 'Last used')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((entry) => (
            <TableRow key={entry.userId}>
              <TableCell className="text-[13.5px] text-foreground">{entry.userName}</TableCell>
              <TableCell className="hidden text-right text-[13px] tabular-nums text-muted-foreground sm:table-cell">
                {formatGroupedNumber(entry.promptTokens)}
              </TableCell>
              <TableCell className="hidden text-right text-[13px] tabular-nums text-muted-foreground sm:table-cell">
                {formatGroupedNumber(entry.completionTokens)}
              </TableCell>
              <TableCell className="text-right text-[13px] font-medium tabular-nums text-foreground">
                {formatGroupedNumber(entry.totalTokens)}
              </TableCell>
              <TableCell className="whitespace-nowrap text-right text-[13px] text-muted-foreground">
                {formatDate(entry.lastUsedAt)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <Panel
      headingLevel="h3"
      flush
      title={t('token_usage_by_member', 'Token Usage by Member')}
      aside={t('current_billing_period', 'Current billing period')}
    >
      {body}
    </Panel>
  );
}
